// Journal du poste de garde : tous les passages (entrées / sorties), filtres, contrôles des sorties de camions PBA, export Excel.
// Responsables : tout l'historique et suppression d'une saisie erronée ; vigile : 7 derniers jours.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, FormControlLabel, IconButton, MenuItem, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import { DeleteOutline, FileDownloadOutlined, ShieldOutlined } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Panel } from '../../components/ui';
import { useAuth } from '../../context/AuthContext';
import { gardeAPI } from '../../services/api';
import { apiError } from '../../utils/format';
import { ecrireExcel } from '../../utils/excel';
import { CAT, detailPassage, titrePassage } from './Garde';

const RES = { AUTORISEE: ['Sortie autorisée', 'success'], ECART: ['Écart', 'warning'], REFUSEE: ['Sortie refusée', 'error'], NON_CONTROLE: ['Non contrôlée', 'warning'] };
const pad = (n) => String(n).padStart(2, '0');
const jour = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fr = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)} ${iso.slice(11, 16)}` : '—');
const duree = (a, b) => {
  if (!a || !b) return '';
  const m = Math.max(0, Math.round((Date.parse(b) - Date.parse(a)) / 60000));
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${pad(m % 60)}`;
};

const Journal = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { isAdmin, hasRole } = useAuth();
  const resp = isAdmin || hasRole('sous_admin');
  const [f, setF] = useState({ du: jour(new Date(Date.now() - 6 * 86400000)), au: jour(new Date()), categorie: '', q: '', alertes: params.get('alertes') === '1' });
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const charger = useCallback(() => gardeAPI.passages({ du: f.du, au: f.au, categorie: f.categorie || undefined, q: f.q || undefined, alertes: f.alertes ? 1 : undefined })
    .then((r) => setD(r.data)).catch((e) => setError(apiError(e))), [f]);
  useEffect(() => { const t = setTimeout(charger, 250); return () => clearTimeout(t); }, [charger]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const suppr = async (p) => {
    if (!window.confirm(`Supprimer ce passage (${titrePassage(p)}) ? À réserver aux saisies erronées.`)) return;
    try { const r = await gardeAPI.supprimer(p.id); setInfo(r.data.message); charger(); } catch (e) { setError(apiError(e)); }
  };
  const exporter = async () => {
    const lignes = [
      [{ value: `Poste de garde — du ${d.du.split('-').reverse().join('/')} au ${d.au.split('-').reverse().join('/')}`, fontWeight: 'bold' }],
      ['Catégorie', 'Qui / véhicule', 'Détail', 'Immatriculation', "Heure d'entrée", 'Heure de sortie', 'Durée', 'BL', 'Contrôle', 'Anomalies', 'Observations', 'Entrée par', 'Sortie par'],
      ...d.passages.map((p) => [CAT[p.categorie].label, titrePassage(p), detailPassage(p), p.immatriculation || '', p.entree ? p.entree.replace('T', ' ').slice(0, 16) : '',
        p.sortie ? p.sortie.replace('T', ' ').slice(0, 16) : '', duree(p.entree, p.sortie), p.bl_numero || '', p.resultat ? RES[p.resultat][0] : '',
        (p.controle?.anomalies || []).join(' ; '), p.observations || '', p.entree_par_nom || '', p.sortie_par_nom || '']),
    ];
    await ecrireExcel(lignes, `poste-de-garde-${d.du}-${d.au}.xlsx`, { largeurs: [18, 24, 34, 14, 16, 16, 10, 16, 16, 40, 30, 12, 12] });
  };
  const alertes = d ? d.passages.filter((p) => ['ECART', 'REFUSEE', 'NON_CONTROLE'].includes(p.resultat)).length : 0;

  return (
    <Layout title="Journal du poste de garde" plain
      actions={<>
        <Button startIcon={<ShieldOutlined />} onClick={() => navigate('/garde')}>Poste de garde</Button>
        <Button startIcon={<FileDownloadOutlined />} disabled={!d?.passages.length} onClick={exporter}>Excel</Button>
      </>}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}
      <Panel sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" type="date" label="Du" value={f.du} onChange={set('du')} InputLabelProps={{ shrink: true }} />
          <TextField size="small" type="date" label="Au" value={f.au} onChange={set('au')} InputLabelProps={{ shrink: true }} />
          <TextField size="small" select label="Catégorie" value={f.categorie} onChange={set('categorie')} sx={{ minWidth: 190 }}>
            <MenuItem value="">Toutes</MenuItem>
            {Object.entries(CAT).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Rechercher (nom, immatriculation, BL…)" value={f.q} onChange={set('q')} sx={{ minWidth: 260, flex: 1 }} />
          <FormControlLabel control={<Switch checked={f.alertes} onChange={set('alertes')} />} label="Anomalies seulement" />
        </Box>
        {!resp && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 1 }}>Le journal des 7 derniers jours est consultable ici ; l'historique complet est réservé aux responsables.</Typography>}
      </Panel>
      {d && (
        <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 1 }}>
          {d.passages.length} passage(s){alertes ? ` · ` : ''}{alertes > 0 && <b style={{ color: '#B4232A' }}>{alertes} anomalie(s) à la sortie de camions PBA</b>}
        </Typography>
      )}
      <Panel flush>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Catégorie</TableCell><TableCell>Qui / véhicule</TableCell><TableCell>Heure d'entrée</TableCell><TableCell>Heure de sortie</TableCell>
                <TableCell>Contrôle / observations</TableCell><TableCell>Vigile</TableCell>{resp && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {d?.passages.map((p) => {
                const C = CAT[p.categorie];
                return (
                  <TableRow key={p.id} hover sx={p.resultat && p.resultat !== 'AUTORISEE' ? { bgcolor: p.resultat === 'REFUSEE' ? '#FCEDEE' : '#FDF6E7' } : undefined}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}><C.icon fontSize="small" sx={{ color: C.couleur }} />{C.label}</Box></TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{titrePassage(p)}</Typography>
                      <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{[detailPassage(p), p.categorie !== 'VISITEUR' && p.categorie !== 'EMPLOYE' ? null : p.piece && `pièce ${p.piece}`].filter(Boolean).join(' · ')}</Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fr(p.entree)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.sortie ? fr(p.sortie) : (p.entree ? <Chip size="small" color="info" label="sur le site" /> : '—')}
                      {p.entree && p.sortie && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{duree(p.entree, p.sortie)}</Typography>}</TableCell>
                    <TableCell sx={{ maxWidth: 420 }}>
                      {p.resultat && <Chip size="small" color={RES[p.resultat][1]} label={`${RES[p.resultat][0]}${p.bl_numero ? ` · ${p.bl_numero}` : ''}`} sx={{ mb: 0.5 }} />}
                      {p.controle?.comptes && Object.keys(p.controle.comptes).length > 0 && (
                        <Typography sx={{ fontSize: 12.5 }}>Poteaux : {Object.entries(p.controle.comptes).map(([t, n]) => `${t} ${n ?? '?'}/${p.controle.attendus?.[t] ?? '?'}`).join(', ')}</Typography>
                      )}
                      {(p.controle?.anomalies || []).map((a) => <Typography key={a} sx={{ fontSize: 12.5, color: 'error.main' }}>• {a}</Typography>)}
                      {p.observations && <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{p.observations}</Typography>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>{[p.entree_par_nom, p.sortie_par_nom].filter(Boolean).filter((x, i, a) => a.indexOf(x) === i).join(' / ')}</TableCell>
                    {resp && <TableCell padding="checkbox"><Tooltip title="Supprimer (saisie erronée)"><IconButton size="small" onClick={() => suppr(p)}><DeleteOutline fontSize="small" /></IconButton></Tooltip></TableCell>}
                  </TableRow>
                );
              })}
              {d && !d.passages.length && <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>Aucun passage sur cette période.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>
    </Layout>
  );
};

export default Journal;
